// src/components/workspace/PlaceholderReviewDialog.tsx
// Approval gate for the deterministic placeholder resolver. Shows what will be
// filled (token → value) and what still needs a human, before anything is saved.

import React from 'react';
import { X, Check, Loader2, Wand2, AlertTriangle } from 'lucide-react';
import type { PlaceholderScan } from '../../lib/placeholders';

interface PlaceholderReviewDialogProps {
  scan: PlaceholderScan | null;
  applying: boolean;
  onApply: () => void;
  onCancel: () => void;
}

export function PlaceholderReviewDialog({ scan, applying, onApply, onCancel }: PlaceholderReviewDialogProps) {
  if (!scan) return null;
  const { filled, unresolved } = scan;
  // De-dupe for display (the same token often appears many times).
  const filledUniq = Array.from(new Map(filled.map((f) => [`${f.token}=>${f.value}`, f])).values());
  const unresolvedUniq = Array.from(new Map(unresolved.map((u) => [u.token, u])).values());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-bw-surface rounded-xl shadow-xl border border-bw-border w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between p-4 border-b border-bw-border">
          <div className="flex items-start gap-2">
            <Wand2 className="h-5 w-5 text-bw-primary mt-0.5" />
            <div>
              <h3 className="font-semibold text-bw-ink">Fill placeholders</h3>
              <p className="text-xs text-bw-muted">
                {filled.length} placeholder{filled.length === 1 ? '' : 's'} fillable from your profile · {unresolvedUniq.length} need your input
              </p>
            </div>
          </div>
          <button onClick={onCancel} disabled={applying} className="p-1 hover:bg-bw-surface-subtle rounded disabled:opacity-50">
            <X className="h-4 w-4 text-bw-muted" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {filledUniq.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-success uppercase tracking-wide mb-2">Will fill ({filled.length})</p>
              <div className="space-y-1.5">
                {filledUniq.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-3.5 w-3.5 text-success flex-shrink-0" />
                    <code className="text-xs bg-bw-surface-subtle px-1.5 py-0.5 rounded text-bw-muted">{f.token}</code>
                    <span className="text-bw-muted">→</span>
                    <span className="text-bw-ink font-medium truncate">{f.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unresolvedUniq.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-fit-moderate uppercase tracking-wide mb-2">Needs your input ({unresolvedUniq.length})</p>
              <div className="space-y-1.5">
                {unresolvedUniq.map((u, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="h-3.5 w-3.5 text-fit-moderate flex-shrink-0" />
                    <code className="text-xs bg-bw-surface-subtle px-1.5 py-0.5 rounded text-bw-muted">{u.token}</code>
                    <span className="text-xs text-bw-muted">· {u.section} — fill in the editor or add it to your profile</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filled.length === 0 && unresolvedUniq.length === 0 && (
            <p className="text-sm text-bw-muted text-center py-8">No placeholders found — nothing to fill.</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-bw-border">
          <p className="text-xs text-bw-muted">Nothing is saved until you click Apply.</p>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} disabled={applying} className="px-3 py-1.5 text-sm text-bw-muted hover:text-bw-ink disabled:opacity-50">
              Cancel
            </button>
            <button
              onClick={onApply}
              disabled={applying || filled.length === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-success text-white rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50"
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {applying ? 'Applying…' : `Fill ${filled.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlaceholderReviewDialog;
